import React, { useState } from 'react';
import { Box, Flex, Button } from 'rebass';
import { Label, Input } from '@rebass/forms';
import { FiEdit } from 'react-icons/fi';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import config from 'config';
import auth from 'services/auth';
import serviceIntegrations from 'services/integrations';
import services from 'services/services';
import { useIncentiveProvidersQuery, INCENTIVE_PROVIDERS_QUERY_KEY } from 'api/useIncentiveProvidersQuery';
import { useToastHelper } from 'hooks/useToastHelper';
import Portal from 'components/Portal';
import NiceModal from 'components/NiceModal';
import {
    IntegrationItem,
    IntegrationHeader,
    IntegrationActionButtons,
    IntegrationDivider,
    IntegrationStatus,
    ConnectButton,
    DisconnectButton
} from 'components/IntegrationTile';

// A provider that collects its own fields can be re-entered; one that redirects to
// authorize cannot (Connect sends the customer back to the provider instead).
const isEditable = provider => provider.connect_strategy === 'credentials_form';

const hasSummary = provider => Object.keys(provider.summary_fields).length > 0;

// Connect and Edit share one form, so the button says which one the customer opened.
const submitLabel = (provider, isBusy) => {
    if (provider.connected) {
        return isBusy ? 'Saving...' : 'Save';
    }

    return isBusy ? 'Connecting...' : 'Connect';
};

/**
 * Settings > Integrations tiles for the incentive providers a customer connects.
 *
 * Every tile is rendered from the provider manifest: its copy, logo, help link and
 * connect control all come from the provider's descriptor on the API. Adding a
 * provider therefore needs no change here — but it does need the logo asset named
 * by `descriptor.logo` in app/images/integrations, *and* its own
 * `!file-loader` line in app/app.js. The src below is built by concatenation, so
 * an asset webpack never emits fails silently as a broken image.
 *
 * Two connect strategies are supported, chosen per provider by the manifest:
 *  - `oauth_redirect`  — send the browser to the provider to authorize us
 *  - `credentials_form` — collect the fields the provider declares
 *
 * Which provider-side account a payout draws on is not decided here: a provider
 * that holds several is chosen per study, in Study Settings.
 */
export function IncentiveProviderTiles() {
    const queryClient = useQueryClient();
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const { data: providers, isLoading, isError, refetch } = useIncentiveProvidersQuery();

    // The provider whose connect modal is open, plus the values typed into it.
    const [connecting, setConnecting] = useState(null);
    const [fieldValues, setFieldValues] = useState({});

    const closeModal = () => {
        setConnecting(null);
        setFieldValues({});
    };

    const { mutate: connect, isLoading: isConnecting } = useMutation({
        mutationFn: ({ provider, fields }) => serviceIntegrations.connectIncentiveProvider(provider.name, fields),
        onSuccess: (data, { provider }) => {
            queryClient.setQueryData(INCENTIVE_PROVIDERS_QUERY_KEY, data);
            addSuccessToast(provider.connected ? `Updated ${provider.label}` : `Connected ${provider.label}`);
            closeModal();
        },
        onError: (error, { provider }) => {
            // The server's message is written for the customer (a rejected key, a
            // platform with nothing to pay from), so it is shown rather than swallowed.
            addErrorToast(services.parseAndTrackXhrErrors(error) || `Could not connect ${provider.label}`);
        }
    });

    // variables is the provider the in-flight call was made with: one mutation
    // instance backs every tile, so a bare isDisconnecting greys out every provider's
    // Disconnect button while one of them is being disconnected.
    const { mutate: disconnect, isLoading: isDisconnecting, variables: disconnecting } = useMutation({
        mutationFn: provider => serviceIntegrations.disconnectIncentiveProvider(provider.name),
        onSuccess: (data, provider) => {
            queryClient.setQueryData(INCENTIVE_PROVIDERS_QUERY_KEY, data);
            addSuccessToast(`Disconnected ${provider.label}`);
        },
        // Same treatment as connect: the server's message is written for the customer
        // (a payout in flight, a study still pointing at this provider), and
        // parseAndTrackXhrErrors is also what reports the failure to the tracker — the
        // Tremendous tile this replaces used it, so discarding the error here would be
        // a step down from master.
        onError: (error, provider) =>
            addErrorToast(services.parseAndTrackXhrErrors(error) || `Could not disconnect ${provider.label}`)
    });

    // Re-entering the form for a connected provider starts from what it already
    // knows, so rotating one key doesn't mean retyping the fields around it: every
    // field below is `required` and a connect replaces the whole secret set, so a
    // blank form would make the customer re-enter their platform name to change
    // their API key. Only non-secret fields can be prefilled — the summary is the
    // one piece of stored state the API sends back, and it is defined as never
    // carrying a secret. A provider whose summary keys don't line up with its field
    // names just prefills nothing.
    const prefillFor = provider => {
        if (!provider.connected) {
            return {};
        }

        return provider.credential_fields.reduce(
            (values, field) =>
                field.secret || !provider.summary?.[field.name]
                    ? values
                    : { ...values, [field.name]: provider.summary[field.name] },
            {}
        );
    };

    const startConnect = provider => {
        if (provider.connect_strategy === 'oauth_redirect') {
            // The provider redirects back to /settings/integrations once authorized.
            window.location = `${config.API_URL}${provider.oauth_authorize_path}?account_id=${auth.getAccountId()}`;
            return;
        }

        setConnecting(provider);
        setFieldValues(prefillFor(provider));
    };

    const submitConnect = event => {
        event.preventDefault();

        connect({ provider: connecting, fields: { ...fieldValues } });
    };

    // Rendering nothing leaves a blank gap that pops in, while every other integration
    // on this page shows a placeholder — so say something instead.
    if (isLoading) {
        return (
            <IntegrationItem>
                <Box role="status" className="fs-body-14 color-text-secondary" p={3}>
                    Loading incentive integrations…
                </Box>
            </IntegrationItem>
        );
    }

    // A failed manifest fetch used to render nothing at all, so the whole feature
    // silently vanished from this page and the customer concluded their integration had
    // broken. Say so instead, and offer a way back.
    if (isError || !providers) {
        return (
            <IntegrationItem>
                <Box role="alert" className="fs-body-14 color-text-secondary" p={3}>
                    Could not load incentive integrations.{' '}
                    <Button variant="link" onClick={() => refetch()}>
                        Try again
                    </Button>
                </Box>
            </IntegrationItem>
        );
    }

    return (
        <>
            {providers.map(provider => (
                <IntegrationItem key={provider.name}>
                    <IntegrationHeader>
                        <img src={`${config.UI_SUFFIX}/${provider.logo}`} alt={provider.label} />
                        <Box flex={1}>
                            <Box className="fs-title-18 color-text-primary" mb={2}>
                                {provider.label}
                            </Box>
                            <Box className="fs-body-14 color-text-secondary" mb={3}>
                                {provider.description}
                            </Box>
                            {provider.help_url && (
                                <Box className="fs-body-14">
                                    <a href={provider.help_url} target="_blank">
                                        How to set up {provider.label}.
                                    </a>
                                </Box>
                            )}
                        </Box>
                        <IntegrationActionButtons>
                            {provider.connected ? (
                                <DisconnectButton
                                    disabled={isDisconnecting && disconnecting?.name === provider.name}
                                    onClick={() => disconnect(provider)}
                                />
                            ) : (
                                <ConnectButton onClick={() => startConnect(provider)} />
                            )}
                        </IntegrationActionButtons>
                    </IntegrationHeader>
                    {provider.connected && (hasSummary(provider) || isEditable(provider)) && (
                        <>
                            <IntegrationDivider />
                            <IntegrationStatus>
                                <Flex></Flex>
                                <Flex className="fs-body-12 color-text-secondary" alignItems="center">
                                    <Box>
                                        {Object.entries(provider.summary_fields).map(([key, label], index) => (
                                            <React.Fragment key={key}>
                                                {index > 0 && '; '}
                                                {label}: <strong>{provider.summary[key] || '—'}</strong>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                    {isEditable(provider) && (
                                        <Button
                                            variant="secondary-gray"
                                            className="!ml-2"
                                            onClick={() => startConnect(provider)}
                                        >
                                            <FiEdit /> Edit
                                        </Button>
                                    )}
                                </Flex>
                            </IntegrationStatus>
                        </>
                    )}
                </IntegrationItem>
            ))}

            <Portal>
                <NiceModal
                    isOpen={!!connecting}
                    onRequestClose={closeModal}
                    style={{ content: { width: '640px' } }}
                    title={connecting ? `${connecting.label} ${connecting.connected ? 'credentials' : 'connect'}` : ''}
                >
                    {connecting && (
                        <form onSubmit={submitConnect}>
                            {connecting.credential_fields.map((field, index) => (
                                <Box key={field.name} mb={3}>
                                    <Label htmlFor={`${connecting.name}-${field.name}`}>{field.label}</Label>
                                    <Input
                                        id={`${connecting.name}-${field.name}`}
                                        type={field.secret ? 'password' : 'text'}
                                        name={field.name}
                                        placeholder={field.placeholder}
                                        // Chrome ignores autoComplete="off" on password
                                        // inputs; "new-password" is the honoured value.
                                        autoComplete={field.secret ? 'new-password' : 'off'}
                                        required
                                        autoFocus={index === 0}
                                        value={fieldValues[field.name] || ''}
                                        onChange={event =>
                                            setFieldValues({ ...fieldValues, [field.name]: event.target.value })
                                        }
                                    />
                                    {field.help && (
                                        <Box className="fs-body-12 color-text-secondary" mt={1}>
                                            {field.help}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                            <Box className="modal-actions">
                                <Button type="button" variant="secondary-gray" mr={3} onClick={closeModal}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="primary" sx={{ marginRight: 0 }} disabled={isConnecting}>
                                    {submitLabel(connecting, isConnecting)}
                                </Button>
                            </Box>
                        </form>
                    )}
                </NiceModal>
            </Portal>
        </>
    );
}

export default IncentiveProviderTiles;
